import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";
import { TextArea } from "@/components/TextArea";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import {
  occurrenceActionSchema,
  type OccurrenceActionFormData,
} from "@/schemas/occurrenceSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useReturnOccurrence } from "@/hooks/useOccurrences";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

type RefundOcurrenceDialogProps = {
  showDialog: boolean;
  setShowDialog: (open: boolean) => void;
  occurrenceId: string;
};

const reasonForReturn = [
  { value: "IMPRECISE_LOCATION", label: "Localização imprecisa ou incorreta" },
  {
    value: "INSUFFICIENT_DESCRIPTION",
    label: "Descrição insuficiente do problema",
  },
  { value: "MISSING_EVIDENCE", label: "Fotos/evidências necessárias" },
  { value: "INVALID_CONTACT", label: "Dados de contato inválidos" },
  { value: "CONTRADICTORY_INFO", label: "Informações contraditórias" },
  { value: "OTHER", label: "Outro" },
];

export const RefundOcurrenceDialog = (props: RefundOcurrenceDialogProps) => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] =
    useState<OccurrenceActionFormData | null>(null);
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<OccurrenceActionFormData>({
    resolver: zodResolver(occurrenceActionSchema),
    defaultValues: {
      reasonId: "",
      description: "",
      messageTemplateId: "",
      customMessage: "",
    },
  });
  const refundOccurrence = useReturnOccurrence();

  const handleClose = () => {
    reset();
    setShowConfirmation(false);
    props.setShowDialog(false);
  };

  const submitRefundOccurrence = (data: OccurrenceActionFormData) => {
    setFormDataToSubmit(data);
    setShowConfirmation(true);
  };

  const confirmRefundOccurrence = async () => {
    if (!formDataToSubmit) return;

    await refundOccurrence.mutateAsync(
      {
        occurrenceId: props.occurrenceId,
        reason: formDataToSubmit.reasonId,
        requiredInformation: formDataToSubmit.description,
        messageToUser: formDataToSubmit.customMessage,
      },
      {
        onSuccess: () => {
          navigate("/occurrences");
          toast.success("Ocorrência devolvida ao cidadão com sucesso.");
          setShowConfirmation(false);
          props.setShowDialog(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={props.showDialog}
      onOpenChange={(open) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Devolver ocorrência ao cidadão
          </DialogTitle>
          <DialogDescription>
            Informe o motivo da devolução e as informações que precisam ser
            complementadas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(submitRefundOccurrence)}>
          <div className="grid gap-2 py-4">
            <Controller
              name="reasonId"
              control={control}
              render={({ field }) => (
                <>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger title="Motivo da devolução" isRequired>
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasonForReturn.map((motivo) => (
                        <SelectItem key={motivo.value} value={motivo.value}>
                          {motivo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.reasonId && (
                    <span className="text-sm text-red-500">
                      {errors.reasonId.message}
                    </span>
                  )}
                </>
              )}
            />

            <TextArea
              {...register("description")}
              title="Descrição da devolução"
              placeholder="Descreva quais informações o cidadão deve fornecer..."
              isRequired
              errorMessage={errors.description?.message}
            />
            {/* <Controller
              name="messageTemplateId"
              control={control}
              render={({ field }) => (
                <>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger title="Template de mensagem personalizada">
                      <SelectValue placeholder="Selecione o template" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasonForReturn.map((motivo) => (
                        <SelectItem key={motivo.value} value={motivo.value}>
                          {motivo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.messageTemplateId && (
                    <span className="text-sm text-red-500">
                      {errors.messageTemplateId.message}
                    </span>
                  )}
                </>
              )}
            /> */}
            <TextArea
              {...register("customMessage")}
              title="Mensagem personalizada"
              placeholder="Escreva uma mensagem clara e educada ao cidadão explicando o motivo da devolução e as informações necessárias."
              errorMessage={errors.customMessage?.message}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              className="cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={refundOccurrence.isPending}
              className="bg-orange-500 hover:bg-orange-600 text-white cursor-pointer"
              onClick={() => {}}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Devolver
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar devolução da ocorrência
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja devolver esta ocorrência ao cidadão? Esta
              ação não poderá ser revertida e o cidadão será notificado sobre as
              informações que precisam ser complementadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRefundOccurrence}
              className="bg-orange-500 hover:bg-orange-600 text-white cursor-pointer"
            >
              Confirmar Devolução
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
